import { doc, writeBatch } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import type { TradingAccount } from '../types';
import {
  getAuthBasicTierLimitedHint,
  resolveIsBasicLimitedTier,
} from './basicTierLimits';

export const BASIC_TIER_ACCOUNT_LIMIT = 1;

const ACCOUNTS_COLLECTION = 'accounts';

let syncInFlight: Promise<void> | null = null;
let lastPersistedSignature = '';

function normalizeCreatedAt(value: unknown): number {
  if (!value) return Number.MAX_SAFE_INTEGER;

  if (typeof value === 'object' && value !== null) {
    if ('toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const parsed = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
    }
    if ('seconds' in value && typeof (value as { seconds?: number }).seconds === 'number') {
      return (value as { seconds: number }).seconds * 1000;
    }
  }

  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
}

export function sortAccountsByCreation(accounts: TradingAccount[]): TradingAccount[] {
  return [...accounts].sort((a, b) => {
    const dateDiff = normalizeCreatedAt(a.createdAt) - normalizeCreatedAt(b.createdAt);
    if (dateDiff !== 0) return dateDiff;
    return a.id.localeCompare(b.id);
  });
}

export function getBasicTierPrimaryAccountId(accounts: TradingAccount[]): string | null {
  const activeAccounts = accounts.filter(account => !account.deleted);
  if (activeAccounts.length === 0) return null;
  return sortAccountsByCreation(activeAccounts)[0]?.id ?? null;
}

export function applyBasicTierAccountState(
  accounts: TradingAccount[],
  isLimitedTier: boolean,
): TradingAccount[] {
  const primaryAccountId = getBasicTierPrimaryAccountId(accounts);

  return accounts.map((account) => {
    if (account.deleted) return account;

    if (!isLimitedTier) {
      if (account.disabledByBasicTier) {
        return {
          ...account,
          isDisabled: false,
          disabledByBasicTier: false,
          disabledAt: null,
        };
      }
      return account;
    }

    if (account.id === primaryAccountId) {
      return {
        ...account,
        isDisabled: false,
        disabledByBasicTier: false,
        disabledAt: null,
      };
    }

    return {
      ...account,
      isDisabled: true,
      disabledByBasicTier: true,
      disabledAt: account.disabledAt || new Date().toISOString(),
    };
  });
}

function buildPersistSignature(accounts: TradingAccount[], isLimitedTier: boolean): string {
  const primaryAccountId = getBasicTierPrimaryAccountId(accounts);
  const relevant = accounts
    .filter(account => !account.deleted)
    .map(account => [
      account.id,
      account.isDisabled ? '1' : '0',
      account.disabledByBasicTier ? '1' : '0',
    ].join(':'))
    .sort()
    .join('|');

  return `${isLimitedTier ? 'basic' : 'premium'}:${primaryAccountId ?? 'none'}:${relevant}`;
}

function needsPersistUpdate(original: TradingAccount, desired: TradingAccount): boolean {
  return (
    Boolean(original.isDisabled) !== Boolean(desired.isDisabled)
    || Boolean(original.disabledByBasicTier) !== Boolean(desired.disabledByBasicTier)
  );
}

export async function syncBasicTierAccountLock(
  accounts: TradingAccount[],
  isLimitedTierOverride?: boolean,
): Promise<TradingAccount[]> {
  const user = getAuth().currentUser;
  if (!user) return accounts;

  const authHint = getAuthBasicTierLimitedHint();
  const isLimitedTier = typeof isLimitedTierOverride === 'boolean'
    ? isLimitedTierOverride
    : typeof authHint === 'boolean'
      ? authHint
      : await resolveIsBasicLimitedTier(user.uid);

  const desiredAccounts = applyBasicTierAccountState(accounts, isLimitedTier);
  const desiredSignature = buildPersistSignature(desiredAccounts, isLimitedTier);

  const updates = accounts
    .map((account, index) => ({ original: account, desired: desiredAccounts[index] }))
    .filter(({ original, desired }) => needsPersistUpdate(original, desired));

  if (updates.length === 0 || desiredSignature === lastPersistedSignature) {
    return desiredAccounts;
  }

  const runSync = async () => {
    const batch = writeBatch(db);

    updates.forEach(({ original, desired }) => {
      batch.update(doc(db, ACCOUNTS_COLLECTION, original.id), {
        isDisabled: Boolean(desired.isDisabled),
        disabledByBasicTier: Boolean(desired.disabledByBasicTier),
        disabledAt: desired.isDisabled ? (desired.disabledAt || new Date().toISOString()) : null,
        updatedAt: new Date().toISOString(),
      });
    });

    await batch.commit();
    lastPersistedSignature = desiredSignature;
  };

  try {
    if (!syncInFlight) {
      syncInFlight = runSync().finally(() => {
        syncInFlight = null;
      });
    } else {
      syncInFlight = syncInFlight.then(runSync);
    }
    await syncInFlight;
  } catch (error) {
    console.error('[basicTierAccountLock] Failed to persist account lock state:', error);
  }

  return desiredAccounts;
}

export function clearBasicTierAccountLockCache(): void {
  lastPersistedSignature = '';
}

export { isBasicLimitedFromAuthState } from './basicTierLimits';
